import { FullEvent } from '@snowflake/core-ui';
import { ABExperimentName, DEFAULT_GROUP, SignupEventType } from '@signup/shared';
import { v4 } from 'uuid';
import { parseQueryParams } from './ParseQueryParams';
import { SignupApi } from '../api/signupApi';
import { MutinyExperience } from './MutinyExperience';

export enum SignupSteps {
  INITIAL_LOAD = 'initial_load',
  FIRST_FORM = 'first_form',
  SECOND_FORM = 'second_form',
  THANK_YOU_PAGE = 'thank_you_page',
}

export enum AzureSteps {
  AZURE_INITIAL_LOAD = 'azure_initial_load',
  AZURE_FORM = 'azure_form',
  AZURE_THANK_YOU_PAGE = 'azure_thank_you_page',
}

export enum AwsSteps {
  AWS_INITIAL_LOAD = 'aws_initial_load',
  AWS_FORM = 'aws_form',
  AWS_THANK_YOU_PAGE = 'aws_thank_you_page',
}

export enum SignupEventName {
  SIGNUP_FORM_RENDERING = 'signup_form_rendering',
  SIGNUP_FORM_TWO_VALIDATION_ERROR_ON_SUBMIT = 'signup_form_two_validation_error_on_submit',
  SIGNUP_FORM_TWO_COMPLETE = 'signup_form_two_complete',
  SIGNUP_FORM_ONE_VALIDATION_ERROR_ON_SUBMIT = 'signup_form_one_validation_error_on_submit',
  SIGNUP_FORM_ONE_COMPLETE = 'signup_form_one_complete',
  SIGNUP_VALIDATE_EMAIL = 'signup_validate_email',
  SIGNUP_SELECT_EDITION = 'signup_select_edition',
  SIGNUP_SELECT_CLOUD = 'signup_select_cloud',
  SIGNUP_SELECT_REGION = 'signup_select_region',
  SIGNUP_FILTER_REGION_SWITCH = 'signup_filter_region_switch',
  SIGNUP_COMPUTE_DEFAULT_REGION = 'signup_compute_default_region',
  SIGNUP_SALES_ASSISTED_ENG_ERROR = 'signup_sales_assisted_eng_error',
  SIGNUP_SALES_ASSISTED_OWNER_ERROR = 'signup_sales_assisted_owner_error',
  SIGNUP_ACCOUNT_CREATION_ERROR = 'signup_account_creation_error',
  SIGNUP_SALES_ASSISTED_SUCCESS = 'signup_sales_assisted_success',
  SIGNUP_SUCCESS = 'signup_success',
  SIGNUP_SUPPRESSED = 'signup_suppressed',
  EXIT_SIGNUP_INITIAL_LOAD = 'exit_signup_initial_load',
  EXIT_SIGNUP_FIRST_FORM = 'exit_signup_first_form',
  EXIT_SIGNUP_SECOND_FORM = 'exit_signup_second_form',
  EXIT_SIGNUP_SURVEY_PAGE = 'exit_signup_survey_page',
  EXIT_SIGNUP_THANK_YOU_PAGE = 'exit_signup_thank_you_page',
  SIGNUP_ERROR_BOUNDARY_EXCEPTION = 'signup_error_boundary_exception',
  SIGNUP_ROOT_ERROR = 'signup_root_error',
  GOTO_LOGIN_PAGE_LINK_CLICKED = 'goto_login_page_link_clicked',
  FETCH_VISIBLE_REGIONS_FAILS = 'fetch_visible_regions_fails',
  GET_LISTING_INFO_ERROR = 'signup_get_listing_info_error',
  GET_LISTING_INFO_START = 'signup_get_listing_info_start',
  GET_LISTING_INFO_SUCCESS = 'signup_get_listing_info_success',
  POST_SIGNUP_ACQUISITION_SOURCE_SURVEY = 'post_signup_acquisition_source_survey',
  // Modified/Treatment Flow Events
  SIGNUP_MODIFIED_FLOW_RENDERING = 'signup_modified_flow_rendering',
  SIGNUP_MODIFIED_FORM_ONE_COMPLETE = 'signup_modified_form_one_complete',
  SIGNUP_MODIFIED_FORM_ONE_VALIDATION_ERROR = 'signup_modified_form_one_validation_error',
  SIGNUP_MODIFIED_FORM_TWO_OPENED = 'signup_modified_form_two_opened',
  SIGNUP_MODIFIED_FORM_TWO_COMPLETE = 'signup_modified_form_two_complete',
  SIGNUP_MODIFIED_FORM_TWO_VALIDATION_ERROR = 'signup_modified_form_two_validation_error',
  SIGNUP_MODIFIED_BACK_TO_FORM_ONE = 'signup_modified_back_to_form_one',
  SIGNUP_MODIFIED_PLAN_OPTIONS_CLICKED = 'signup_modified_plan_options_clicked',
  SIGNUP_MODIFIED_SIGNUP_CTA_CLICKED = 'signup_modified_signup_cta_clicked',
  SIGNUP_MODIFIED_SUCCESS = 'signup_modified_success',
  SIGNUP_MODIFIED_ERROR = 'signup_modified_error',
  SIGNUP_MODIFIED_SUPPRESSED = 'signup_modified_suppressed',
  SIGNUP_MODIFIED_TRY_AGAIN_PAGE_SHOWN = 'signup_modified_try_again_page_shown',
  SIGNUP_MODIFIED_SUCCESS_CARD_SHOWN = 'signup_modified_success_card_shown',
  SIGNUP_MODIFIED_FAILURE_CARD_SHOWN = 'signup_modified_failure_card_shown',
  EXIT_SIGNUP_MODIFIED_FORM_ONE = 'exit_signup_modified_form_one',
  EXIT_SIGNUP_MODIFIED_FORM_TWO = 'exit_signup_modified_form_two',
  // Toggle Events
  SIGNUP_UNIFIED_VIEW_SWITCH = 'signup_unified_view_switch',
  // Cortex Code install block (V2) interactions
  CORTEX_CODE_COPY_CLI_COMMAND = 'cortex_code_copy_cli_command',
  CORTEX_CODE_COPY_CLI_COMMAND_ERROR = 'cortex_code_copy_cli_command_error',
  CORTEX_CODE_DOWNLOAD_DESKTOP = 'cortex_code_download_desktop',
  // Waitlist Events
  CORTEX_CODE_WAITLIST_FORM_VALIDATION_ERROR_ON_SUBMIT = 'cortex_code_waitlist_form_validation_error_on_submit',
  CORTEX_CODE_WAITLIST_FORM_COMPLETE = 'cortex_code_waitlist_form_complete',
  CORTEX_CODE_WAITLIST_FORM_SUBMIT_FAILURE = 'cortex_code_waitlist_form_submit_failure',
  CORTEX_CODE_WAITLIST_FORM_SUBMIT_SUCCESS = 'cortex_code_waitlist_form_submit_success',
  CORTEX_CODE_WAITLIST_FORM_REQUEST_STARTED = 'cortex_code_waitlist_form_request_started',
  CORTEX_CODE_WAITLIST_FORM_SUBMIT_SUPPRESSED = 'cortex_code_waitlist_form_submit_suppressed',
  CORTEX_CODE_WAITLIST_FORM_ERROR_CARD_SHOWN = 'cortex_code_waitlist_form_error_card_shown',
  CORTEX_CODE_WAITLIST_FORM_SUCCESS_CARD_SHOWN = 'cortex_code_waitlist_form_success_card_shown',
  // Bot detection
  SIGNUP_BOT_DETECTION = 'signup_bot_detection',
  // OIDC (Google / Microsoft) social signup events
  SIGNUP_OIDC_START_ERROR = 'signup_oidc_start_error',
  OIDC_BUTTONS_SHOWN = 'oidc_buttons_shown',
  OIDC_BUTTONS_HIDDEN_MUMBAI = 'oidc_buttons_hidden_mumbai',
  OIDC_BUTTONS_HIDDEN_SPECIAL_LINK = 'oidc_buttons_hidden_special_link',
  OIDC_BUTTONS_HIDDEN_UNSUPPORTED = 'oidc_buttons_hidden_unsupported',
  OIDC_BUTTON_CLICKED = 'oidc_button_clicked',
  OIDC_REDIRECT_STARTED = 'oidc_redirect_started',
  OIDC_START_API_ERROR = 'oidc_start_api_error',
  OIDC_CALLBACK_RECEIVED = 'oidc_callback_received',
  OIDC_CALLBACK_MISSING_PARAMS = 'oidc_callback_missing_params',
  OIDC_CALLBACK_FEATURE_DISABLED = 'oidc_callback_feature_disabled',
  OIDC_ACCOUNT_DETAILS_SHOWN = 'oidc_account_details_shown',
  OIDC_ACCOUNT_DETAILS_SUBMITTED = 'oidc_account_details_submitted',
  OIDC_EXISTING_ACCOUNTS_FOUND = 'oidc_existing_accounts_found',
  OIDC_SIGNUP_SUCCESS = 'oidc_signup_success',
  OIDC_SIGNUP_ERROR = 'oidc_signup_error',
  OIDC_REDIRECT_TO_SNOWSIGHT = 'oidc_redirect_to_snowsight',
  OIDC_EXISTING_ACCOUNT_SELECTED = 'oidc_existing_account_selected',
  OIDC_CREATE_NEW_FROM_PICKER = 'oidc_create_new_from_picker',
  OIDC_EXIT_ACCOUNT_DETAILS_FORM = 'oidc_exit_account_details_form',
  OIDC_EXIT_EXISTING_ACCOUNTS_PICKER = 'oidc_exit_existing_accounts_picker',
}

export enum AzureEventName {
  AZURE_FORM_VALIDATION_ERROR_ON_SUBMIT = 'azure_form_validation_error_on_submit',
  AZURE_FORM_COMPLETE = 'azure_form_complete',
  AZURE_VALIDATE_EMAIL = 'azure_validate_email',
  AZURE_ACCOUNT_CREATION_ERROR = 'azure_account_creation_error',
  AZURE_SUCCESS = 'azure_success',
  EXIT_AZURE_INITIAL_LOAD = 'exit_azure_initial_load',
  EXIT_AZURE_FORM = 'exit_azure_form',
  EXIT_AZURE_THANK_YOU_PAGE = 'exit_azure_thank_you_page',
  AZURE_ROOT_ERROR = 'azure_root_error',
  AZURE_TOKEN_ERROR = 'azure_token_error',
}

export enum GcpEventName {
  GCP_ROOT_ERROR = 'gcp_root_error',
}

export enum AwsEventName {
  AWS_FORM_VALIDATION_ERROR_ON_SUBMIT = 'aws_form_validation_error_on_submit',
  AWS_FORM_COMPLETE = 'aws_form_complete',
  AWS_SUCCESS = 'aws_success',
  AWS_VALIDATE_EMAIL = 'aws_validate_email',
  EXIT_AWS_INITIAL_LOAD = 'exit_aws_initial_load',
  EXIT_AWS_FORM = 'exit_aws_form',
  EXIT_AWS_THANK_YOU_PAGE = 'exit_aws_thank_you_page',
  AWS_ACCOUNT_ADD_SUBSCRIPTION_ERROR = 'aws_account_add_error',
}

//list of events that should be flushed immediately
//TODO: use the logger's immediate flag to signal when to flush the queue right away once core-ui supports this.
const immediatelyFlushEvents: string[] = [
  SignupEventName.EXIT_SIGNUP_INITIAL_LOAD,
  SignupEventName.EXIT_SIGNUP_FIRST_FORM,
  SignupEventName.EXIT_SIGNUP_SECOND_FORM,
  SignupEventName.EXIT_SIGNUP_SURVEY_PAGE,
  SignupEventName.EXIT_SIGNUP_THANK_YOU_PAGE,
  SignupEventName.EXIT_SIGNUP_MODIFIED_FORM_ONE,
  SignupEventName.EXIT_SIGNUP_MODIFIED_FORM_TWO,
  SignupEventName.SIGNUP_MODIFIED_ERROR,
  SignupEventName.SIGNUP_MODIFIED_SUCCESS,
  AzureEventName.AZURE_TOKEN_ERROR,
  AzureEventName.EXIT_AZURE_INITIAL_LOAD,
  AzureEventName.EXIT_AZURE_FORM,
  AzureEventName.EXIT_AZURE_THANK_YOU_PAGE,
  AwsEventName.EXIT_AWS_INITIAL_LOAD,
  AwsEventName.EXIT_AWS_FORM,
  AwsEventName.EXIT_AWS_THANK_YOU_PAGE,
  // OIDC events that fire immediately before a navigation/redirect — flush before unload
  SignupEventName.OIDC_REDIRECT_STARTED,
  SignupEventName.OIDC_REDIRECT_TO_SNOWSIGHT,
  SignupEventName.OIDC_SIGNUP_SUCCESS,
  SignupEventName.OIDC_SIGNUP_ERROR,
  SignupEventName.OIDC_EXISTING_ACCOUNT_SELECTED,
  // Page-exit events fired from the beforeunload listener itself -- the page is already
  // closing, so these must flush immediately or they're lost.
  SignupEventName.OIDC_EXIT_ACCOUNT_DETAILS_FORM,
  SignupEventName.OIDC_EXIT_EXISTING_ACCOUNTS_PICKER,
];

// SocialLoginButtons stashes the pre-redirect formId under this sessionStorage key before
// handing off to the external IdP, so it survives the full page load /oidc/callback does on
// return. Shared here (rather than re-declaring the literal) so SignupRoot's formId resolution
// can never drift from the key SocialLoginButtons/OidcCallbackPage read and write.
export const OIDC_FORM_ID_STORAGE_KEY = 'oidcFormId';

// Resolves the formId for the SignupLogger instance that SignupRoot constructs once per page
// load. /oidc/callback is reached via a full page load returning from the external IdP redirect
// -- a plain v4() there mints a brand-new id sharing nothing with the pre-redirect
// oidc_button_clicked event, which is why pre- and post-redirect telemetry could never be joined
// (SignupLogger.processEvent stamps every event's data.formId with this constructor-bound value,
// overriding whatever the caller passed in). Restoring the original id here, before the logger is
// constructed, means every event on the callback page -- including the ones OidcCallbackPage
// already tags with its own formId -- ends up sharing the one id. Falls back to a fresh id when
// sessionStorage is empty (a cold landing on /oidc/callback, or a session that started before
// this fix existed) so telemetry never ships an empty formId.
export function resolveFormId(pathname: string): string {
  if (pathname === '/oidc/callback') {
    return sessionStorage.getItem(OIDC_FORM_ID_STORAGE_KEY) || v4();
  }
  return v4();
}

export class SignupLogger {
  queue: FullEvent[];
  timer: number | null; //reference to the timer returned by setTimeout
  private modifiedSignupFlowExperimentGroup: string;

  constructor(
    private formId: string,
    modifiedSignupFlowExperimentGroup: string,
    private interval: number = 30000, //milliseconds to wait before executing sendTelemetry()
  ) {
    this.queue = [];
    this.timer = null;
    this.modifiedSignupFlowExperimentGroup = modifiedSignupFlowExperimentGroup;
  }

  processEvent(log: FullEvent): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [signupParams, marketingParams] = parseQueryParams((window as any).location.search);
    const mutinyData = MutinyExperience.getExperienceSummaryForLogging();

    const isModifiedSignupFlow =
      this.modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';

    const logWithFormId: FullEvent = {
      ...log,
      data: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(log.data as { [key: string]: any }),
        formId: this.formId,
        timestamp: new Date().toISOString(),
        parsedParams: { ...signupParams, ...marketingParams },
        fullUrl: window.location.href,
        ...mutinyData, // Add Mutiny data here (flattened for easier querying)
        // AB Experiment tracking - critical for analysis
        abExperiment: ABExperimentName.MODIFIED_SIGNUP_FLOW,
        experimentGroup: this.modifiedSignupFlowExperimentGroup || DEFAULT_GROUP,
        isModifiedSignupFlow: isModifiedSignupFlow,
      },
    };
    this.queue.push(logWithFormId);

    if (immediatelyFlushEvents.includes(logWithFormId['event'])) {
      if (typeof this.timer === 'number') {
        window.clearTimeout(this.timer);
      }
      this.sendTelemetry();
    } else if (this.queue.length === 1) {
      //added the first event to the queue, start the timer
      this.timer = window.setTimeout(this.sendTelemetry.bind(this), this.interval);
    }
  }

  sendTelemetry(): void {
    if (!this.queue.length) {
      return;
    }
    const localQueue = this.queue;
    this.queue = [];
    this.timer = null;
    handleTelemetryRequest(localQueue);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logError(error: { component: string; event: string; data: { [key: string]: any } }): void {
    const mutinyData = MutinyExperience.getExperienceSummaryForLogging();

    const isModifiedSignupFlow =
      this.modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';

    //Build the error event
    const event: FullEvent = {
      ...error,
      type: SignupEventType.UI_ERROR,
      location: [error.component],
      data: {
        ...error.data,
        formId: this.formId,
        timestamp: new Date().toISOString(),
        fullUrl: window.location.href,
        ...mutinyData, // Add Mutiny data here (flattened for easier querying)
        // AB Experiment tracking - critical for analysis
        abExperiment: ABExperimentName.MODIFIED_SIGNUP_FLOW,
        experimentGroup: this.modifiedSignupFlowExperimentGroup || DEFAULT_GROUP,
        isModifiedSignupFlow: isModifiedSignupFlow,
      },
      interaction: false,
      flags: {},
    };
    handleTelemetryRequest([event]);
  }
}

function handleTelemetryRequest(events: FullEvent[]) {
  if (!navigator.sendBeacon) {
    // non-blocking.
    void SignupApi.sendTelemetry(events);
  } else {
    void SignupApi.sendTelemetryWithBeacon(events);
  }
}
